import { z } from "zod";

export const carIdSchema = z.enum(["ae86", "s13", "fd3s", "r32", "s2000"]);
export type CarId = z.infer<typeof carIdSchema>;

const stat = z.number().int().min(1).max(10);

const axleGripSchema = z.object({
  mu: z.number().positive(),
  peakSlipDeg: z.number().positive(),
  falloff: z.number().min(0).max(1),
  falloffSpan: z.number().positive(),
});

/** Full tuning for the vehicle model (SPEC §4, §5). Structurally matches `CarParams` in packages/sim. */
export const carPhysicsSchema = z.object({
  mass: z.number().positive(),
  cogHeight: z.number().positive(),
  wheelbase: z.number().positive(),
  frontWeight: z.number().min(0.3).max(0.7),
  yawInertiaFactor: z.number().positive(),
  yawDamping: z.number().nonnegative(),
  length: z.number().positive(),
  width: z.number().positive(),
  combinedSlip: z.number().min(0).max(1),
  weightTransferGain: z.number().nonnegative(),
  wheelRadius: z.number().positive(),
  drivetrain: z.enum(["RWD", "AWD"]),
  awdFrontSplit: z.number().min(0).max(1),
  torqueCurve: z
    .array(z.object({ rpm: z.number().nonnegative(), torque: z.number().nonnegative() }))
    .min(2),
  idleRpm: z.number().positive(),
  redlineRpm: z.number().positive(),
  launchRpm: z.number().positive(),
  gearRatios: z.array(z.number().positive()).min(1),
  finalDrive: z.number().positive(),
  drivetrainEfficiency: z.number().positive().max(1),
  upshiftRpm: z.number().positive(),
  downshiftRpm: z.number().positive(),
  shiftTicks: z.number().int().nonnegative(),
  topSpeedKmh: z.number().positive(),
  dragCoeff: z.number().nonnegative(),
  rollingResistance: z.number().nonnegative(),
  brakeDecel: z.number().positive(),
  brakeBias: z.number().min(0).max(1),
  engineBraking: z.number().nonnegative(),
  backwardDecel: z.number().nonnegative(),
  reverse: z.object({
    accel: z.number().positive(),
    topSpeedKmh: z.number().positive(),
    engageSpeed: z.number().positive(),
  }),
  gripFront: axleGripSchema,
  gripRear: axleGripSchema,
  handbrake: z.object({
    rearGrip: z.number().positive().max(1),
    brakeDecel: z.number().nonnegative(),
    attack: z.number().positive().max(1),
    release: z.number().positive().max(1),
  }),
  steering: z.object({
    lockDeg: z.number().positive(),
    highSpeedFactor: z.number().positive().max(1),
    slideOpenDeg: z.number().positive(),
    speed: z.number().positive(),
  }),
  assists: z.object({
    counterSteer: z.number().min(0).max(1),
    counterSteerStartDeg: z.number().nonnegative(),
    counterSteerRangeDeg: z.number().positive(),
    angleCapDeg: z.number().positive(),
    angleCapGain: z.number().nonnegative(),
    throttleSlew: z.number().positive(),
  }),
});

export const carSchema = z.object({
  id: carIdSchema,
  displayName: z.string().min(1),
  drivetrain: z.enum(["RWD", "AWD"]),
  colour: z.string().min(1),
  engineProfile: z.enum(["inline4", "inline4-highrev", "rotary", "straight6"]),
  popUpHeadlights: z.boolean(),
  stats: z.object({
    power: stat,
    weight: stat,
    grip: stat,
    driftEase: stat,
  }),
  blurb: z.string().min(1),
  physics: carPhysicsSchema,
});
export type Car = z.infer<typeof carSchema>;
export const carsSchema = z.array(carSchema).length(5);

export const driverSchema = z.object({
  id: z.enum(["driver_m", "driver_f"]),
  displayName: z.string().min(1),
});
export type Driver = z.infer<typeof driverSchema>;
export const driversSchema = z.array(driverSchema).length(2);

export const medalsSchema = z.object({
  bronze: z.number().int().positive(),
  silver: z.number().int().positive(),
  gold: z.number().int().positive(),
  legend: z.number().int().positive(),
});

const controlPoint = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
  width: z.number().positive(),
  bank: z.number(),
});

export const surfaceSchema = z.enum(["dry", "paint", "wet"]);

/** Track data (SPEC §6). Scenery, sky and music fields arrive with the visual phases. */
export const trackSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  displayName: z.string().min(1),
  parTimeSeconds: z.number().positive(),
  medals: medalsSchema,
  /** Grid position, m along the track. */
  startDistance: z.number().nonnegative(),
  /** Finish line, m before the end of the spline; the road runs on past it. */
  finishFromEnd: z.number().nonnegative(),
  surfaces: z.array(
    z.object({ fromPoint: z.number().int().nonnegative(), surface: surfaceSchema }),
  ),
  wallType: z.enum(["guard-rail", "concrete", "cliff", "building"]),
  controlPoints: z.array(controlPoint).min(2),
});
export type Track = z.infer<typeof trackSchema>;

export const scoringSchema = z.object({
  timeScoreBase: z.number().positive(),
  crashPenaltySeconds: z.number().nonnegative(),
  cleanMultiplier: z.object({
    none: z.number().positive(),
    scrapes: z.number().positive(),
    clean: z.number().positive(),
  }),
  drift: z.object({
    minSlipAngleDeg: z.number().positive(),
    minSpeedKmh: z.number().positive(),
    chainLinkSeconds: z.number().positive(),
    chainBankSeconds: z.number().positive(),
    maxMultiplier: z.number().positive(),
  }),
});
export type Scoring = z.infer<typeof scoringSchema>;

/** World-level simulation tuning: track sampling, surfaces and walls. */
export const simTuningSchema = z.object({
  sampleSpacing: z.number().positive(),
  searchWindow: z.number().int().positive(),
  lowSpeed: z.number().positive(),
  surfaces: z.object({
    dry: z.number().positive(),
    paint: z.number().positive(),
    wet: z.number().positive(),
  }),
  wall: z.object({
    crashNormalSpeed: z.number().positive(),
    scrapeDecel: z.number().nonnegative(),
    scrapeLoss: z.number().min(0).max(1),
    restitution: z.number().min(0).max(1),
    yawDamping: z.number().min(0).max(1),
    crashLockTicks: z.number().int().nonnegative(),
  }),
});
export type SimTuningData = z.infer<typeof simTuningSchema>;
